import React, { useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileDown, BookOpen, HelpCircle, ChevronDown, ChevronUp, Monitor, Calendar, FileText, Clock, TrendingUp, GraduationCap, DollarSign, LogOut, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const FAQS = [
  { q: 'How do I mark my attendance?', a: 'Go to "Attendance" from the sidebar, click on a date cell, select your status (P, UP, WO, etc.), and submit. Your manager will approve it.' },
  { q: 'How do I apply for leave?', a: 'Navigate to "Leave", click "Apply Leave", select the leave type, dates, and category (Full Day / Half Day / Multiple Days), add a reason, and submit.' },
  { q: 'What is Leave Encashment?', a: 'Unused Annual Leave from the previous year is shown under "Leave Encashment". After March 1st it is marked as "Processed".' },
  { q: 'How do I download my training certificate?', a: 'Go to "Training", find the completed training, and click "Download Certificate". Certificates are downloadable within 30 days of issue.' },
  { q: 'I did not receive the login OTP.', a: 'Check your spam/junk folder. If not received within 2 minutes, go back and try logging in again. Contact HR if the issue persists.' },
  { q: 'My leave balance seems incorrect.', a: 'Balances are auto-calculated based on your joining date and employee type. Contract employees get prorated balances. Contact HR for clarification.' },
];

const PORTALS = [
  { name: 'Dashboard', desc: 'Overview of your leave balance, stats & activity', icon: Monitor, path: '/dashboard', color: 'bg-blue-50 text-blue-600 border-blue-200' },
  { name: 'Attendance', desc: 'Mark & track daily attendance', icon: Calendar, path: '/attendance', color: 'bg-green-50 text-green-600 border-green-200' },
  { name: 'Leave', desc: 'Apply for leaves & view balance', icon: FileText, path: '/leaves', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  { name: 'Overtime', desc: 'Log & track overtime hours', icon: Clock, path: '/overtime', color: 'bg-orange-50 text-orange-600 border-orange-200' },
  { name: 'Performance', desc: 'View scores & stack ranking', icon: TrendingUp, path: '/performance', color: 'bg-purple-50 text-purple-600 border-purple-200' },
  { name: 'Training', desc: 'Complete trainings & earn certificates', icon: GraduationCap, path: '/training', color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  { name: 'Payslips', desc: 'View & download salary slips', icon: DollarSign, path: '/payslips', color: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
  { name: 'Approvals', desc: 'Approve attendance & leave requests', icon: CheckSquare, path: '/my-approvals', color: 'bg-teal-50 text-teal-600 border-teal-200' },
  { name: 'Holidays', desc: 'View holiday calendar', icon: Calendar, path: '/holidays', color: 'bg-pink-50 text-pink-600 border-pink-200' },
  { name: 'Exit Portal', desc: 'Initiate separation process', icon: LogOut, path: '/exit-portal', color: 'bg-red-50 text-red-600 border-red-200' },
];

const HelpPage = ({ user, setUser }) => {
  const [downloading, setDownloading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  const handleDownloadRunbook = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/help/runbook`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'GBT_Employee_Runbook.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Runbook downloaded successfully');
    } catch {
      toast.error('Failed to download runbook');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50" data-testid="help-page">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />

      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto w-full">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Help Center</h1>
          <p className="text-gray-600">Guides, documentation, and quick navigation to all portals</p>
        </div>

        {/* Runbook Download */}
        <Card className="mb-6 border-l-4 border-l-blue-600" data-testid="runbook-card">
          <CardContent className="flex items-center justify-between py-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Employee Runbook</h2>
                <p className="text-sm text-gray-500">Complete guide covering all portals — Dashboard, Attendance, Leave, Performance, Training, Payslip, and Manager tools.</p>
              </div>
            </div>
            <Button
              onClick={handleDownloadRunbook}
              disabled={downloading}
              className="gap-2 bg-blue-600 hover:bg-blue-700 shrink-0"
              data-testid="download-runbook-button"
            >
              <FileDown className="w-4 h-4" />
              {downloading ? 'Generating...' : 'Download (.docx)'}
            </Button>
          </CardContent>
        </Card>

        {/* Quick Navigation Grid */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Navigation</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {PORTALS.map((portal) => (
              <button
                key={portal.path}
                onClick={() => navigate(portal.path)}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all hover:shadow-md hover:-translate-y-0.5 ${portal.color}`}
                data-testid={`nav-${portal.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <portal.icon className="w-6 h-6" />
                <span className="font-medium text-sm">{portal.name}</span>
                <span className="text-xs opacity-70 text-center leading-tight">{portal.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <Card data-testid="faq-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-lg">
              <HelpCircle className="w-5 h-5 text-amber-600" />
              Frequently Asked Questions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {FAQS.map((faq, i) => (
              <div key={i} className="border rounded-lg" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-800 text-sm">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4 text-sm text-gray-600 border-t pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HelpPage;
